import type { PlayerEvent } from '@mlm/shared';
import {
  applyEvent,
  createSnapshotStore,
  type PlaybackAdapter,
  type PlaybackState,
  type PlayerSnapshot,
} from './adapter';
import { readFullscreenEnv, resolveFullscreenMode } from './fullscreen';

/** What an app-owned speed control offers. iOS hides the native one entirely. */
export const NATIVE_PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

type WebkitVideo = HTMLVideoElement & {
  webkitPreservesPitch?: boolean;
  webkitEnterFullscreen?: () => void;
};

function readBuffered(video: HTMLVideoElement): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const { buffered } = video;
  for (let i = 0; i < buffered.length; i += 1) out.push([buffered.start(i), buffered.end(i)]);
  return out;
}

function stateOf(video: HTMLVideoElement): PlaybackState {
  if (video.ended) return 'ended';
  if (video.paused) return 'paused';
  if (video.readyState < 3) return 'buffering';
  return 'playing';
}

/**
 * `<video>` half of the adapter. Deliberately does NOT own the `src`: the host
 * assigns `video.src` imperatively so a React re-render (or a `key` remount)
 * can never yank the learner back to 0:00 — the mechanism polish batch 1
 * shipped and this layer must preserve.
 */
export function createNativeAdapter(video: HTMLVideoElement): PlaybackAdapter {
  const store = createSnapshotStore({
    status: 'idle',
    rates: NATIVE_PLAYBACK_RATES,
    muted: video.muted,
    volume: video.volume,
  });
  let destroyed = false;
  // Held so it can be re-applied: iOS resets playbackRate on a source load and
  // when returning from the native fullscreen player.
  let desiredRate = 1;

  const push = (event: PlayerEvent, extra: Partial<PlayerSnapshot> = {}) => {
    if (destroyed) return;
    const patch = applyEvent(store.get(), event);
    store.patch({ ...(patch ?? {}), ...extra });
    store.emit(event);
  };

  const markReady = () => {
    if (store.get().status !== 'ready') push({ type: 'ready' });
  };

  const syncState = () => {
    const next = stateOf(video);
    if (next !== store.get().state) push({ type: 'state', state: next });
  };

  const applyRate = () => {
    if (video.playbackRate !== desiredRate) video.playbackRate = desiredRate;
  };

  const onLoadedMetadata = () => {
    markReady();
    applyRate();
    if (Number.isFinite(video.duration) && video.duration > 0) {
      push({ type: 'duration', duration: video.duration });
    }
  };
  // iOS on cellular / Low Power Mode treats preload="metadata" as "none", so
  // `loadedmetadata` may never arrive before the first tap. Any of these three
  // proves the element is live enough to drive the UI.
  const onLoadedData = () => {
    markReady();
    applyRate();
  };
  const onDurationChange = () => {
    if (Number.isFinite(video.duration) && video.duration > 0) {
      push({ type: 'duration', duration: video.duration });
    }
  };
  const onTimeUpdate = () => {
    push(
      {
        type: 'time',
        currentTime: video.currentTime,
        duration:
          Number.isFinite(video.duration) && video.duration > 0 ? video.duration : undefined,
      },
      { buffered: readBuffered(video) },
    );
  };
  const onProgress = () => store.patch({ buffered: readBuffered(video) });
  const onPlay = () => {
    markReady();
    syncState();
  };
  const onPlaying = () => {
    markReady();
    applyRate();
    push({ type: 'state', state: 'playing' });
  };
  const onPause = () => {
    if (!video.ended) push({ type: 'state', state: 'paused' });
  };
  const onWaiting = () => push({ type: 'state', state: 'buffering' });
  const onSeeked = () => push({ type: 'seeked', currentTime: video.currentTime });
  const onEnded = () => push({ type: 'state', state: 'ended' });
  const onRateChange = () => push({ type: 'rate', rate: video.playbackRate });
  const onVolumeChange = () => store.patch({ muted: video.muted, volume: video.volume });
  const onError = () => {
    push({
      type: 'error',
      code: video.error?.code,
      message: video.error?.message ?? 'media_error',
    });
  };
  // Returning from Apple's native fullscreen player resets the rate.
  const onWebkitEndFullscreen = () => applyRate();

  const handlers: Array<[string, EventListener]> = [
    ['loadedmetadata', onLoadedMetadata],
    ['loadeddata', onLoadedData],
    ['durationchange', onDurationChange],
    ['timeupdate', onTimeUpdate],
    ['progress', onProgress],
    ['play', onPlay],
    ['playing', onPlaying],
    ['pause', onPause],
    ['waiting', onWaiting],
    ['seeked', onSeeked],
    ['ended', onEnded],
    ['ratechange', onRateChange],
    ['volumechange', onVolumeChange],
    ['error', onError],
    ['webkitendfullscreen', onWebkitEndFullscreen],
  ];
  for (const [name, fn] of handlers) video.addEventListener(name, fn);

  const captionTracks = () => Array.from(video.textTracks ?? []);

  return {
    play: () => video.play(),
    pause: () => video.pause(),
    seek(seconds) {
      // No preview distinction natively: setting currentTime IS the seek.
      video.currentTime = Math.max(0, seconds);
    },
    async setRate(rate) {
      desiredRate = rate;
      const v = video as WebkitVideo;
      // Keep voices intelligible at 1.5x-2x; both spellings are still in use.
      if ('preservesPitch' in v) v.preservesPitch = true;
      if ('webkitPreservesPitch' in v) v.webkitPreservesPitch = true;
      video.playbackRate = rate;
      return true;
    },
    setMuted(muted) {
      video.muted = muted;
    },
    setVolume(volume01) {
      video.volume = Math.min(1, Math.max(0, volume01));
    },
    setCaptions(lang) {
      const tracks = captionTracks();
      for (const track of tracks) {
        track.mode = lang != null && track.language === lang ? 'showing' : 'disabled';
      }
      store.patch({ captionsLang: lang, captionsAvailable: tracks.length > 0 });
    },
    getSnapshot: () => ({
      ...store.get(),
      captionsAvailable: captionTracks().length > 0,
    }),
    subscribe: store.subscribe,
    capabilities: {
      rate: true,
      volume: true,
      captions: true,
      fullscreen: resolveFullscreenMode(readFullscreenEnv(video)),
    },
    destroy() {
      destroyed = true;
      for (const [name, fn] of handlers) video.removeEventListener(name, fn);
      store.clear();
    },
  };
}
